import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Col, Row } from 'react-bootstrap';
import { Button } from '@praktio/common-ui';

import SettingsContent from './SettingsContent';
import { updateUser } from '../../../ducks/users';
import PageHeader from '../../common/PageHeader/PageHeader';
import PageFooter from '../../common/PageFooter/PageFooter';
import FeedbackModal from '../../common/FeedbackModal/FeedbackModal';
import NetworkErrorModal from '../../common/modals/NetworkErrorModal/NetworkErrorModal';

import './Settings.scss';
import './SettingsContent.scss';

const Settings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const settingsContentRef = useRef<IRemoteUserSettingsFunctionsProps>(null);

  const [hasChanges, setHasChanges] = useState(false);
  const [disableSave, setDisableSave] = useState(!hasChanges);
  const [savedChanges, setSavedChanges] = useState(false);
  const [showNetworkErrorModal, setShowNetworkErrorModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [currentError, setCurrentError] = useState<string | undefined>();

  // Handle disable state of Save button and
  // the appearance of the Saved badge.
  useEffect(() => {
    setDisableSave(!hasChanges);
    if (hasChanges && savedChanges) {
      setSavedChanges(false);
    }
  }, [hasChanges, savedChanges]);

  const handleNetworkError = (error: IError) => {
    setShowNetworkErrorModal(true);
    setCurrentError(error.message);
  };

  const handleSubmit = async () => {
    setDisableSave(true);
    try {
      const validatedData = await settingsContentRef.current?.validateData();

      if (validatedData?.hasChanges && validatedData?.formData) {
        await dispatch(updateUser(validatedData.formData, true, navigate));
        settingsContentRef.current?.clearErrors();
      }

      setSavedChanges(true);
    } catch (e) {
      settingsContentRef.current?.handleError(e as IError);
    }

    setDisableSave(false);
  };

  return (
    <div className="Settings">
      <Helmet>
        <title>Settings - Praktio</title>
      </Helmet>
      <PageHeader pageName="Settings" savedChanges={savedChanges} unsavedChanges={hasChanges}>
        <Button
          btnStyle="primary"
          className="UserSettingsSaveButton form-submit-button"
          disabled={!!disableSave}
          aria-disabled={!!disableSave}
          onClick={async () => await handleSubmit()}
        >
          SAVE
        </Button>
      </PageHeader>
      <div className="Settings-content">
        <Col xs={12} md={8} lg={6}>
          <Row>
            <SettingsContent
              ref={settingsContentRef}
              handleNetworkError={handleNetworkError}
              notifyHasChanges={setHasChanges}
            />
          </Row>
        </Col>
      </div>
      <PageFooter />
      <NetworkErrorModal
        show={showNetworkErrorModal}
        onHide={() => setShowNetworkErrorModal(false)}
        submitFeedback={() => {
          setShowNetworkErrorModal(false);
          setShowFeedbackModal(true);
        }}
      />
      <FeedbackModal
        show={showFeedbackModal}
        onHide={() => setShowFeedbackModal(false)}
        errorMessage={currentError}
      />
    </div>
  );
};

export default Settings;
